import { Formik } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TModalsState, setModalsData } from "../../api/store/commonSlice";
import {
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
} from "../../components/admin/formFields";
import FTModal from "../../components/common/FTModal";
import { TIMEZONE_OPTIONS } from "../../constants/constants";
import { useAccountTimezone } from "../../hooks/timezone";
import { dateInitialValues, dateValidationSchema } from "./validation";

export type MapFleetFiltersData = {
  date_time: string;
  timezone: string;
  calculatedUnixTimestamp: number;
};

type ModalMapFleetFiltersProps = {
  loadingData: boolean;
  savedFilterValues?: MapFleetFiltersData; // filter values saved from last user change
  onFilterChange?: (data: MapFleetFiltersData) => void;
};

const ModalMapFleetFilters: FC<ModalMapFleetFiltersProps> = ({
  loadingData,
  savedFilterValues,
  onFilterChange,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "mapFleet.vehicleFilter",
  });

  const { tzForDisplay } = useAccountTimezone();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showVehicleFilter: false }));
  };

  return (
    <FTModal
      visible={modalsState.showVehicleFilter}
      heading={t("heading")}
      onHide={hideModal}
      containerStyle="max-w-[calc(100vw-4rem)] w-[480px]"
      disableOverlay={true}
    >
      <Formik
        initialValues={{
          ...(savedFilterValues ?? dateInitialValues),
          timezone: tzForDisplay,
        }}
        validationSchema={dateValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          onFilterChange && onFilterChange(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          dirty,
          setFieldValue,
          setFieldTouched,
          resetForm,
          submitForm,
        }) => {
          return (
            <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
              <AdminFormFieldInput
                customWrapperClass="col-span-6"
                label={t(`date_time`)}
                type="datetime-picker"
                id={`date_time`}
                name={`date_time`}
                value={values.date_time}
                maxTime={new Date()}
                onDatepickerChange={(fieldName: string, value: any) => {
                  setFieldValue(fieldName, value);
                }}
                onBlur={handleBlur}
                error={errors.date_time}
                touched={touched.date_time}
                detailsFormField={true}
                size="lg"
              />

              <AdminFormFieldDropdown
                label={t("timezone")}
                id="timezone"
                name="timezone"
                value={values.timezone}
                options={[
                  {
                    value: "",
                    label: t("all_timezones"),
                  },
                  ...TIMEZONE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  })),
                ]}
                onChange={(e) => setFieldValue("timezone", e?.value)}
                onBlur={(e) => {
                  setFieldTouched("timezone", true);
                  handleBlur(e);
                }}
                error={errors.timezone}
                touched={touched.timezone}
              />

              <AdminFormFieldSubmit
                label={tMain("confirm")}
                type="submit"
                variant="primary"
                // onClick={hideModal} // disabled to let the form submit
                disabled={loadingData}
              />

              <AdminFormFieldSubmit
                label={tMain("cancel")}
                type="button"
                variant="danger-transparent"
                onClick={hideModal}
                disabled={loadingData}
              />
            </form>
          );
        }}
      </Formik>
    </FTModal>
  );
};

export default ModalMapFleetFilters;
